import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import type { IResourceAnnouncementItem } from '@/api/types';

import { otherColors } from '@/styles/colors';
import { ResourceAnnouncement } from '@/assets/icons';
import { html } from '@/helpers';
import { media } from '@/styles/theme';
import { Attachment } from '@/components/common';

interface IProps {
  resource: IResourceAnnouncementItem;
}

const AnnouncementPreview = ({ resource }: IProps): React.ReactElement => {
  const { t } = useTranslation();

  return (
    <Container>
      <Title>
        <TitleIcon src={ResourceAnnouncement} />
        <h1>{resource.title}</h1>
      </Title>
      <TextContainer>{html(resource.body || resource.bodyShort)}</TextContainer>
      <AttachmentsTitle>
        {t`resources.files.attachments`} ({resource.attachments?.length ?? 0})
      </AttachmentsTitle>
      <AttachmentsContainer>
        {resource.attachments?.map(attachmentUrl => (
          <Attachment
            key={attachmentUrl}
            fileName={attachmentUrl.substring(attachmentUrl.lastIndexOf('/') + 1)}
            actions={{ preview: attachmentUrl }}
          />
        ))}
      </AttachmentsContainer>
    </Container>
  );
};

export { AnnouncementPreview };

const Container = styled.div`
  margin-top: 24px;
  overflow: auto;

  h1 {
    font-size: 18px;
    font-weight: 300;
    color: ${otherColors.lxc.darkBlue};
    margin: 0;

    ${media.phone} {
      font-size: 30px;
    }
  }
`;

const TitleIcon = styled.img`
  width: 26px;
  height: 26px;
  margin-right: 12px;
  margin-top: 5px;
`;

const Title = styled.div`
  display: flex;
  margin-bottom: 26px;
`;

const TextContainer = styled.div`
  font-size: 14px;
  max-width: 680px;
`;

const AttachmentsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const AttachmentsTitle = styled.h3`
  display: none;
  font-size: 20px;
  font-weight: 300;
  color: ${otherColors.lxc.darkBlue};
  margin-top: 48px;
  margin-bottom: 20px;

  ${media.phone} {
    display: block;
  }
`;
